import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Documentation translations with type safety and error handling
 * Provides centralized access to all Documentation-related translations with performance optimization
 */
export function useDocumentationTranslations() {
  // Use the default translation namespace since documentation.json is merged into translation
  const { t, i18n } = useTranslation('translation');

  // Memoize translation functions to prevent re-renders
  const translations = useMemo(() => {
    // Helper functions for common translation patterns with type safety
    const getDocumentationText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.${key}`, params || {}));
    };

    const getHeaderText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.header.${key}`, params || {}));
    };

    const getTabsText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.tabs.${key}`, params || {}));
    };

    const getStatesText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.states.${key}`, params || {}));
    };

    const getFeedbackText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.feedback.${key}`, params || {}));
    };

    const getFeedbackFormText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.feedback.form.${key}`, params || {}));
    };

    const getFeedbackValidationText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.feedback.validation.${key}`, params || {}));
    };

    const getFeedbackToastText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.feedback.toast.${key}`, params || {}));
    };

    const getFallbackText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.fallback.${key}`, params || {}));
    };

    const getLanguagesText = (key: string, params?: Record<string, any>): string => {
      return String(t(`documentation.languages.${key}`, params || {}));
    };

    // Safe translation wrapper that always returns string
    const tSafe = (key: string, params?: Record<string, any>): string => {
      return String(t(key, params || {}));
    };

    // Helper function to get language display name
    const getLanguageName = (languageCode: string): string => {
      return getLanguagesText(languageCode) || languageCode;
    };

    // Helper for character count display
    const getCharacterCount = (count: number, max: number): string => {
      return getFeedbackFormText('characterCount', { count, max });
    };

    return {
      // Direct translation function with type safety
      t: tSafe,

      // Category-specific helpers
      getDocumentationText,
      getHeaderText,
      getTabsText,
      getStatesText,
      getFeedbackText,
      getFeedbackFormText,
      getFeedbackValidationText,
      getFeedbackToastText,
      getFallbackText,

      // Specialized helpers
      getCharacterCount,
      getLanguageName,

      // Current language
      currentLanguage: i18n.language,

      // Fallback labels (expected by LanguageFallbackBanner)
      getFallbackLabels: {
        title: getFallbackText('title'),
        dismiss: getFallbackText('dismiss'),
      },

      // Header labels (expected by DocumentationPage)
      getHeaderLabels: {
        title: getHeaderText('title'),
        description: getHeaderText('description'),
        platformGuides: getHeaderText('platformGuides'),
        onThisPage: getHeaderText('onThisPage'),
      },

      // Tab labels (expected by DocumentationPage)
      getTabLabels: {
        gettingStarted: {
          label: getTabsText('gettingStarted.label'),
          description: getTabsText('gettingStarted.description'),
        },
        productManagement: {
          label: getTabsText('productManagement.label'),
          description: getTabsText('productManagement.description'),
        },
        dataStructure: {
          label: getTabsText('dataStructure.label'),
          description: getTabsText('dataStructure.description'),
        },
        importExport: {
          label: getTabsText('importExport.label'),
          description: getTabsText('importExport.description'),
        },
        pricingChannels: {
          label: getTabsText('pricingChannels.label'),
          description: getTabsText('pricingChannels.description'),
        },
        teamPermissions: {
          label: getTabsText('teamPermissions.label'),
          description: getTabsText('teamPermissions.description'),
        },
        analyticsReports: {
          label: getTabsText('analyticsReports.label'),
          description: getTabsText('analyticsReports.description'),
        },
        advancedFeatures: {
          label: getTabsText('advancedFeatures.label'),
          description: getTabsText('advancedFeatures.description'),
        },
      },

      // State labels (expected by DocumentationPage)
      getStateLabels: {
        comingSoon: getStatesText('comingSoon'),
      },

      // Direct access to commonly used translations (for backward compatibility)
      main: {
        title: getDocumentationText('title'),
        guides: getDocumentationText('guides'),
        api: getDocumentationText('api'),
        tutorials: getDocumentationText('tutorials'),
        faq: getDocumentationText('faq'),
        search: getDocumentationText('search'),
        notFound: getDocumentationText('notFound'),
        loading: getDocumentationText('loading'),
      },

      // Feedback modal translations
      feedback: {
        title: getFeedbackText('title'),
        description: getFeedbackText('description'),
        form: {
          messageLabel: getFeedbackFormText('messageLabel'),
          messageRequired: getFeedbackFormText('messageRequired'),
          messagePlaceholder: getFeedbackFormText('messagePlaceholder'),
          emailLabel: getFeedbackFormText('emailLabel'),
          emailPlaceholder: getFeedbackFormText('emailPlaceholder'),
          emailHelp: getFeedbackFormText('emailHelp'),
          buttons: {
            cancel: getFeedbackFormText('buttons.cancel'),
            send: getFeedbackFormText('buttons.send'),
            sending: getFeedbackFormText('buttons.sending'),
          },
        },
        validation: {
          messageMinLength: getFeedbackValidationText('messageMinLength'),
          messageMaxLength: getFeedbackValidationText('messageMaxLength'),
          emailInvalid: getFeedbackValidationText('emailInvalid'),
        },
        toast: {
          successTitle: getFeedbackToastText('successTitle'),
          successDescription: getFeedbackToastText('successDescription'),
          errorTitle: getFeedbackToastText('errorTitle'),
          errorDefault: getFeedbackToastText('errorDefault'),
          errorRateLimit: getFeedbackToastText('errorRateLimit'),
        },
      },
    };
  }, [t, i18n.language]);

  return translations;
}

export type DocumentationTranslations = ReturnType<typeof useDocumentationTranslations>;
